import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { setMobileAccessToken } from '@/lib/mobileAuth';
import { isNativeRuntime } from '@/lib/runtime';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUserData } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef (not useState) for the processed flag
    // Set it synchronously at the start to prevent race conditions under StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id found');
          navigate('/', { replace: true });
          return;
        }

        // Exchange session_id for user data
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) {
          throw new Error('Failed to process session');
        }

        const userData = await response.json();
        if (isNativeRuntime() && userData?.access_token) {
          setMobileAccessToken(userData.access_token);
        }
        setUserData(userData);

        // Clear the hash and redirect to dashboard
        window.history.replaceState(null, '', '/app');
        navigate('/app', { replace: true, state: { user: userData } });
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUserData]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-zinc-600 font-medium">Autenticando...</p>
        <p className="text-zinc-400 text-sm mt-1">Por favor, aguarde</p>
      </div>
    </div>
  );
}
